import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Lenis from 'lenis';
import { ArrowLeft } from 'lucide-react';
import { MonkeyLogo } from '../components/MonkeyLogo';

const DISPLAY = "font-['Cabinet_Grotesk',_'Inter',_sans-serif] font-black tracking-tighter";
const BODY = "font-['Satoshi',_'Inter',_sans-serif]";
const EASE_OUT = [0.16, 1, 0.3, 1];
const EFFECTIVE = 'February 21, 2026';

const SECTIONS = [
  {
    h: '1. Who we are',
    p: `LinkMonkey ("LinkMonkey", "we", "us") provides a hosted link‑in‑bio service that lets creators publish a single shareable profile linking out to their social accounts, websites, music, videos, fan pages, tip jar and other public content. By creating an account or otherwise using LinkMonkey you ("you", "user") agree to these Terms of Service. If you do not agree, do not use the service.`,
  },
  {
    h: '2. Accounts',
    p: `You must be 16 or older to create an account. You are responsible for your login credentials and for everything that happens under your account. You agree to provide accurate information and to keep your contact email up to date so we can reach you about security, billing or service issues. We may suspend or terminate accounts that violate these Terms, abuse the service, or pose a security/legal risk.`,
  },
  {
    h: '3. Your content',
    p: `You retain ownership of everything you upload — your avatar, wallpapers, links, bio, fan‑page posts, email‑campaign drafts, etc. You grant LinkMonkey a worldwide, non‑exclusive, royalty‑free license to host, store, transmit, display and process that content solely to operate the service (e.g. rendering your public profile, sending the emails you compose, generating OG preview images). This license ends when you delete the content or your account, except where we are legally required to retain copies.`,
  },
  {
    h: '4. Acceptable use',
    p: `You may not use LinkMonkey to: (a) post or link to content that is illegal, infringes others' rights, or is sexually explicit involving minors; (b) impersonate another person or brand without permission; (c) distribute malware, phishing pages, or deceptive content; (d) run unsolicited bulk email or spam through our campaigns feature; (e) attempt to circumvent rate limits, authentication, billing or storage quotas; (f) scrape, reverse‑engineer or stress‑test the service without written consent. We may remove offending content or suspend offending accounts without notice.`,
  },
  {
    h: '5. Free and Pro plans',
    p: `Core link‑in‑bio features (unlimited links, basic themes, QR code, click counter) are free. Pro features (premium themes, custom fonts, wallpapers, animations, Tip Jar, Fan Page, analytics, timed links, email campaigns, branding removal) are billed €4/month or €40/year via Stripe. Subscriptions auto‑renew until cancelled. You can cancel any time from the Stripe Customer Portal; cancellation takes effect at the end of the current billing period — no pro‑rata refunds. A 7‑day free trial of Pro is available once per account, no card required.`,
  },
  {
    h: '6. Payments and refunds',
    p: `Payments are processed by Stripe under their own terms. Prices are shown in EUR and are inclusive of applicable VAT where required. If a renewal payment fails, Stripe Smart Retries will attempt to recover the charge over several days; we'll email you about the failure with a link to update your card. If payment cannot be recovered, your Pro features automatically downgrade to Free. We don't offer general refunds, but if you were charged in error, contact support@linkmonkey.io within 14 days and we'll review.`,
  },
  {
    h: '7. Affiliate program',
    p: `Pro creators can opt in to our affiliate program and earn commissions on referrals. Self‑referrals are blocked. Payouts require a minimum balance and a valid PayPal email. We reserve the right to withhold or claw back commissions linked to fraudulent signups, chargebacks or violations of these Terms.`,
  },
  {
    h: '8. Service availability and storage',
    p: `LinkMonkey is provided on an "as is" and "as available" basis. We don't promise uninterrupted uptime, and we may schedule maintenance, change features, or impose reasonable storage quotas. File uploads (avatars, wallpapers, fan‑page images) are subject to per‑account storage limits — if you exceed them, uploads will fail with a clear error until quota is restored or upgraded.`,
  },
  {
    h: '9. Third‑party services',
    p: `Your public profile may embed or link to third‑party platforms (YouTube, Spotify, SoundCloud, Instagram, TikTok, X, etc.). Those platforms have their own terms and privacy policies; LinkMonkey is not responsible for their content, availability, or how they handle visitors who click through. Payment processing is performed by Stripe; outbound emails by Resend; file storage by Emergent Object Storage — each under their own terms.`,
  },
  {
    h: '10. Termination',
    p: `You can delete your account at any time from Settings. Deletion permanently removes your profile, links, uploads, fan‑page posts, analytics and subscriptions (your Stripe subscription is cancelled at the same time). We may terminate or suspend your account if you breach these Terms, if required by law, or for prolonged inactivity. On termination, sections 3 (license), 11 (liability) and 12 (governing law) survive.`,
  },
  {
    h: '11. Disclaimers and liability',
    p: `To the maximum extent permitted by law, LinkMonkey is provided without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose and non‑infringement. LinkMonkey is not liable for any indirect, incidental, special, consequential or punitive damages, or for lost profits, revenue, data or goodwill. Our total liability for any claim related to the service will not exceed the greater of (a) the amount you paid us in the 12 months preceding the claim or (b) EUR 50.`,
  },
  {
    h: '12. Changes and governing law',
    p: `We may update these Terms from time to time. Material changes will be announced by email or via a notice in the dashboard at least 14 days before they take effect; continued use of the service after the effective date means you accept the updated Terms. These Terms are governed by the laws of Ireland, without regard to conflict‑of‑laws rules. Disputes will be resolved by the courts of Dublin, Ireland, except where mandatory consumer protection law gives you a right to bring proceedings in your own country.`,
  },
  {
    h: '13. Contact',
    p: `Questions about these Terms? Email support@linkmonkey.io. For privacy and data requests, see our Privacy Policy.`,
  },
];

export default function Terms() {
  useEffect(() => {
    const lenis = new Lenis({ smoothWheel: true, lerp: 0.1 });
    function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  return (
    <div className="min-h-screen bg-[#06070A] text-white" data-testid="terms-page">
      <Helmet>
        <title>Terms of Service — LinkMonkey</title>
        <meta name="description" content="LinkMonkey Terms of Service. The rules of the road for using our link-in-bio platform." />
      </Helmet>

      {/* Subtle grain + radial glow */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.04] bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.85%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>')]" />
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[120vw] h-[60vh] pointer-events-none"
           style={{ background: 'radial-gradient(ellipse at top, rgba(0,85,255,0.18) 0%, transparent 60%)' }} />

      {/* Nav */}
      <nav className="relative z-10 px-6 md:px-10 py-6 flex items-center justify-between max-w-7xl mx-auto">
        <Link to="/" className="flex items-center gap-3 group" data-testid="terms-nav-home">
          <MonkeyLogo size={32} />
          <span className={`${BODY} text-base font-semibold text-white`}>LinkMonkey</span>
        </Link>
        <Link to="/" className={`${BODY} flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors`} data-testid="terms-back-home">
          <ArrowLeft className="w-4 h-4" />
          Back home
        </Link>
      </nav>

      {/* Header */}
      <header className="relative z-10 px-6 md:px-10 pt-12 md:pt-20 pb-10 max-w-3xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE_OUT }}
          className={`${BODY} text-[11px] uppercase tracking-[0.3em] text-white/40 mb-5`}>
          Legal
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE_OUT, delay: 0.05 }}
          className={`${DISPLAY} text-5xl md:text-7xl leading-[0.95] mb-6`}>
          Terms of <em className="italic font-light text-white/80">Service</em>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE_OUT, delay: 0.1 }}
          className={`${BODY} text-white/55 text-base md:text-lg leading-relaxed max-w-2xl`}>
          The plain‑English rules of the road for using LinkMonkey. Read them carefully — by signing up you're agreeing to them.
        </motion.p>
        <p className={`${BODY} text-xs text-white/30 mt-6 font-mono`}>Effective {EFFECTIVE}</p>
      </header>

      {/* Body */}
      <main className="relative z-10 px-6 md:px-10 pb-24 max-w-3xl mx-auto">
        <div className="space-y-10">
          {SECTIONS.map((s, i) => (
            <motion.section
              key={s.h}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5, ease: EASE_OUT, delay: Math.min(i * 0.02, 0.2) }}
              data-testid={`terms-section-${i + 1}`}
            >
              <h2 className={`${DISPLAY} text-2xl md:text-3xl text-white mb-3`}>{s.h}</h2>
              <p className={`${BODY} text-white/65 leading-relaxed text-[15px] md:text-base`}>{s.p}</p>
            </motion.section>
          ))}
        </div>

        <div className="mt-20 pt-10 border-t border-white/10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <p className={`${BODY} text-sm text-white/40`}>
            Also read our{' '}
            <Link to="/privacy" className="text-white/80 hover:text-white underline underline-offset-4" data-testid="terms-link-to-privacy">
              Privacy Policy
            </Link>.
          </p>
          <Link to="/" className={`${BODY} text-sm text-white/50 hover:text-white transition-colors`}>
            ← Back to LinkMonkey
          </Link>
        </div>
      </main>
    </div>
  );
}
